import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { NextIntlClientProvider } from 'next-intl';
import { DefaultSeo } from 'next-seo';

import { GlobalInfoProvider } from '@/utils/hooks/context/useGlobalInfo';

import { LoggedInStoreProvider } from '@/utils/zustand/providers/logged-in-provider';
import { ProfilesStoreProvider } from '@/utils/zustand/providers/profiles-provider';
import { SignUpStoreProvider } from '@/utils/zustand/providers/sign-up-provider';
import { ToggleLayoutPlatformProvider } from '@/utils/zustand/providers/toggle-layout-platform-provider';
import { usePartnerStackCookie } from '@/hooks/utils/usePartnerStackCookie';
import { useUtmDataCookie } from '@/utils/hooks/utils/useUtmDataCookie';

// Scripts for tools
import Tools from '@/components/tools';

import 'public/fonts/fonticon/style.css';
import '../styles/styles.css';
import nextIntlConfig from '../../next-intl.config';
import SEO from '../../next-seo.config';

function App({ Component, pageProps }: AppProps) {
    const router = useRouter();
    useUtmDataCookie();
    usePartnerStackCookie();

    // Ensure we have valid props for NextIntlClientProvider
    const locale = router.locale ?? 'en';
    const messages = pageProps.messages ?? {};

    // Get translated description from messages if available, otherwise use fallback
    const metaDescription = messages?.meta?.description;

    return (
        <>
            <DefaultSeo
                openGraph={{
                    description: metaDescription
                }}
                description={metaDescription}
                {...SEO}
            />
            <NextIntlClientProvider
                locale={locale}
                messages={messages}
                onError={nextIntlConfig.onError}>
                <ToggleLayoutPlatformProvider>
                    <LoggedInStoreProvider>
                        <ProfilesStoreProvider>
                            <SignUpStoreProvider>
                                <GlobalInfoProvider>
                                    <Component {...pageProps} />
                                    <Tools />
                                </GlobalInfoProvider>
                            </SignUpStoreProvider>
                        </ProfilesStoreProvider>
                    </LoggedInStoreProvider>
                </ToggleLayoutPlatformProvider>
            </NextIntlClientProvider>
        </>
    );
}

// This default export is required in a new `pages/_app.js` file.
export default App;
